// eslint configured: rule not present in CRA build
import { useEffect, useMemo, useState } from "react";
import { Flame, Plus, ArrowRight, Sparkles, Play, Wand2 } from "lucide-react";
import { apiGet, apiMutate, client } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Link } from "react-router-dom";
import IdeaModal from "@/components/IdeaModal";
import OnboardingModal from "@/components/OnboardingModal";
import ProductTour from "@/components/ProductTour";
import ScriptModal from "@/components/ScriptModal";
import GuestBanner from "@/components/GuestBanner";
import DemoChecklist from "@/components/DemoChecklist";
import ChannelWidget from "@/components/ChannelWidget";
import { markCoachStep } from "@/lib/coach";
import { runSmartReminders } from "@/lib/notify";

const SEAL = "var(--seal)";
const INK = "var(--ink)";
const INK_55 = "var(--ink-55)";
const INK_72 = "var(--ink-72)";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function StreakHeatmap({ data }) {
  const grid = data?.grid || [];
  const weeks = [];
  for (let i = 0; i < grid.length; i += 7) weeks.push(grid.slice(i, i + 7));

  const colorFor = (n) => {
    if (n <= 0) return "rgba(245,230,194,0.06)";
    if (n === 1) return "rgba(212,160,90,0.30)";
    if (n === 2) return "rgba(212,160,90,0.55)";
    if (n <= 4) return "rgba(212,160,90,0.80)";
    return "var(--seal)";
  };

  return (
    <div className="flex gap-1" data-testid="streak-heatmap">
      {weeks.map((w, wi) => (
        <div key={wi} className="flex flex-col gap-1">
          {w.map((c) => (
            <div
              key={c.date}
              title={`${c.date}: ${c.count}`}
              className="w-3 h-3 rounded-sm"
              style={{ background: colorFor(c.count) }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [streak, setStreak] = useState({ grid: [], current: 0, longest: 0 });
  const [ideas, setIdeas] = useState([]);
  const [goals, setGoals] = useState([]);
  const [showNewIdea, setShowNewIdea] = useState(false);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const [tourDismissed, setTourDismissed] = useState(false);
  const [showScript, setShowScript] = useState(false);
  const [quickIdea, setQuickIdea] = useState("");

  const showOnboarding = !!user && !user.preferences?.onboarded && !onboardingDismissed;
  // The old 5-step informational ProductTour modal now NEVER auto-fires —
  // CoachTour (action-driven) handles new-user onboarding. ProductTour is
  // still mounted so it can be triggered manually from Settings as a backup.
  const showTour =
    !!user && !!user.preferences?.onboarded && !user.preferences?.tour_seen && !tourDismissed && !showOnboarding && false;

  const load = async () => {
    const [t, s, i, g] = await Promise.all([
      apiGet("/tasks", { due_date: todayISO() }),
      apiGet("/streak", { days: 84 }),
      apiGet("/ideas"),
      apiGet("/goals"),
    ]);
    setTasks(t);
    setStreak(s);
    setIdeas(i.slice(0, 8));
    setGoals(g);

    // Smart reminders — fire only when user really needs a nudge. Throttled
    // to max 1 notification per kind per day inside notify.js.
    try {
      const todayKey = new Date().toISOString().slice(0, 10);
      const lastActiveCell = (s?.grid || []).slice().reverse().find((c) => c.count > 0);
      const lastActivityDate = lastActiveCell?.date || null;
      // Days remaining on the nearest active goal (if any).
      const upcoming = (g || [])
        .map((goal) => {
          if (!goal.period_end) return null;
          const end = new Date(goal.period_end + "T00:00:00Z").getTime();
          const today = new Date(todayKey + "T00:00:00Z").getTime();
          const days = Math.round((end - today) / (1000 * 60 * 60 * 24));
          return days >= 0 ? days : null;
        })
        .filter((v) => v !== null)
        .sort((a, b) => a - b)[0];
      runSmartReminders({
        streak: s?.current || 0,
        lastActivityDate,
        goalDueIn: upcoming,
      });
    } catch { /* noop — reminders are non-critical */ }
  };

  useEffect(() => { load(); }, []);

  // Onboarding & Tour are derived from user.preferences directly — no setState in effect.

  const toggleTask = async (task) => {
    await apiMutate("patch", `/tasks/${task.id}`, { completed: !task.completed });
    load();
  };

  const addQuick = async (e) => {
    e.preventDefault();
    if (!quickIdea.trim()) return;
    await apiMutate("post", "/ideas", { title: quickIdea.trim(), platform: "youtube_long", tags: [], stage: "idea" });
    setQuickIdea("");
    markCoachStep("create-idea");
    load();
  };

  const pending = tasks.filter((t) => !t.completed);
  const done = tasks.filter((t) => t.completed);

  return (
    <div className="max-w-6xl mx-auto">
      <GuestBanner user={user} />
      <DemoChecklist user={user} onTriggerScript={() => setShowScript(true)} />
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">
            <span className="seal-dot mr-2 align-middle" /> Today · {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          </div>
          <h1 className="font-heading mt-4 text-4xl md:text-5xl" style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}>
            {greeting()}, <span className="font-display-italic">{user?.name?.split(" ")[0] || "Creator"}</span>.
          </h1>
        </div>
        <button onClick={() => setShowNewIdea(true)} className="btn-primary" data-testid="dashboard-new-idea-btn" data-coach="new-idea">
          <Plus size={14} /> New idea
        </button>
      </div>

      {/* Welcome / walkthrough strip removed — CoachTour handles first-run.
          Users can still replay it any time from Settings. */}
      {false && user && user.preferences?.onboarded && !user.preferences?.tour_seen && (
        <div
          className="mt-7 rounded-2xl p-5 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4"
          style={{
            background: "linear-gradient(135deg, rgba(216,168,87,0.10), rgba(216,168,87,0.03))",
            border: "1px solid rgba(216,168,87,0.30)",
          }}
          data-testid="dashboard-tour-card"
        >
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: "rgba(216,168,87,0.16)", border: "1px solid rgba(216,168,87,0.40)" }}
            >
              <Play size={14} style={{ color: "var(--seal)" }} />
            </div>
            <div>
              <div className="eyebrow mb-1">New here?</div>
              <div className="font-heading text-xl" style={{ fontVariationSettings: "'opsz' 48, 'wght' 500" }}>
                Take the 60-second <span className="font-display-italic">walkthrough</span>.
              </div>
              <div className="text-xs mt-1" style={{ color: INK_55 }}>
                Five stops. The whole studio in under a minute.
              </div>
            </div>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              className="btn-secondary"
              onClick={async () => {
                // Reset 'tour_seen' if previously set so tour can run again from card click.
                if (user?.preferences?.tour_seen) {
                  await client.patch("/auth/tour", { seen: false });
                  refresh?.();
                }
                setTourDismissed(false);
              }}
              data-testid="dashboard-start-tour"
            >
              Start tour <ArrowRight size={12} />
            </button>
            <button
              className="btn-ghost text-xs"
              data-testid="dashboard-dismiss-tour"
              onClick={async () => { await client.patch("/auth/tour", { seen: true }); refresh?.(); }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Quick capture */}
      <form onSubmit={addQuick} className="mt-8 flex gap-2" data-testid="dashboard-quick-capture">
        <input
          value={quickIdea}
          onChange={(e) => setQuickIdea(e.target.value)}
          placeholder="Capture a spark…"
          className="input"
          data-testid="dashboard-quick-input"
        />
        <button type="submit" className="btn-secondary" data-testid="dashboard-quick-save">Save</button>
      </form>

      <div className="mt-6">
        <ChannelWidget />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        {/* Today's tasks */}
        <div className="card md:col-span-2">
          <div className="flex items-center justify-between mb-5">
            <div className="eyebrow">i. Today&apos;s tasks</div>
            <div className="text-xs font-mono" style={{ color: INK_55 }}>{done.length}/{tasks.length}</div>
          </div>
          {tasks.length === 0 ? (
            <div className="text-sm py-6 text-center" style={{ color: INK_55 }}>
              No tasks today. Move ideas into <span style={{ color: SEAL }}>Create</span> on the pipeline.
            </div>
          ) : (
            <ul className="space-y-1" data-testid="dashboard-tasks-list" style={{ borderTop: "1px solid var(--ink-22)" }}>
              {[...pending, ...done].map((t) => (
                <li
                  key={t.id}
                  className="flex items-center gap-3 py-3"
                  style={{ borderBottom: "1px solid var(--ink-22)" }}
                >
                  <button
                    data-testid={`task-toggle-${t.id}`}
                    onClick={() => toggleTask(t)}
                    className="w-4 h-4 rounded-sm flex-shrink-0 flex items-center justify-center transition"
                    style={{
                      background: t.completed ? SEAL : "transparent",
                      border: t.completed ? `2px solid ${SEAL}` : `2px solid var(--ink-38)`,
                    }}
                  >
                    {t.completed && <div className="w-1.5 h-1.5 rounded-[1px]" style={{ background: "var(--bg)" }} />}
                  </button>
                  <span
                    className={`text-sm flex-1 ${t.completed ? "line-through" : ""}`}
                    style={{ color: t.completed ? INK_55 : INK }}
                  >
                    {t.title}
                  </span>
                  {t.source === "pipeline" && <span className="tag-brass">pipeline</span>}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Streak */}
        <div className="card">
          <div className="flex items-center justify-between mb-5">
            <div className="eyebrow">ii. Streak</div>
            <Flame size={14} style={{ color: SEAL }} />
          </div>
          <div className="flex items-baseline gap-2">
            <div className="font-heading text-5xl" style={{ fontVariationSettings: "'opsz' 144, 'wght' 450" }} data-testid="streak-current">
              {streak.current}
            </div>
            <div className="text-xs" style={{ color: INK_55 }}>day{streak.current === 1 ? "" : "s"}</div>
          </div>
          <div className="text-xs mt-1" style={{ color: INK_55 }}>Longest: {streak.longest}</div>
          <div className="mt-5 overflow-x-auto">
            <StreakHeatmap data={streak} />
          </div>
        </div>
      </div>

      {/* Goals + Pipeline preview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div className="card">
          <div className="flex items-center justify-between mb-5">
            <div className="eyebrow">iii. Goals</div>
            <Link to="/goals" className="btn-ghost text-xs" data-testid="dashboard-goals-link">
              Manage <ArrowRight size={12} />
            </Link>
          </div>
          {goals.length === 0 ? (
            <div className="text-sm py-4 text-center" style={{ color: INK_55 }}>
              No goals yet. <Link to="/goals" style={{ color: SEAL, textDecoration: "underline" }}>Set one →</Link>
            </div>
          ) : (
            <div className="space-y-4" data-testid="dashboard-goals-list">
              {goals.slice(0, 4).map((g) => {
                const pct = Math.min(100, Math.round(((g.progress || 0) / g.target) * 100));
                return (
                  <div key={g.id}>
                    <div className="flex justify-between text-xs mb-1.5">
                      <span style={{ color: INK_72 }}>{g.label}</span>
                      <span style={{ color: INK_55 }}>{g.progress || 0}/{g.target}</span>
                    </div>
                    <div className="h-1 rounded-full overflow-hidden" style={{ background: "var(--ink-22)" }}>
                      <div className="h-full transition-all" style={{ width: `${pct}%`, background: SEAL }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-5">
            <div className="eyebrow">iv. Pipeline</div>
            <Link to="/pipeline" className="btn-ghost text-xs" data-testid="dashboard-pipeline-link">
              Open <ArrowRight size={12} />
            </Link>
          </div>
          <div className="text-sm" style={{ color: INK_72 }}>
            {ideas.length} item{ideas.length === 1 ? "" : "s"} in motion
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-4">
            {[
              { id: "idea", label: "Idea" },
              { id: "research", label: "Research" },
              { id: "script", label: "Script" },
              { id: "create", label: "Create" },
              { id: "schedule", label: "Schedule" },
              { id: "published", label: "Publish" },
            ].map(({ id, label }) => {
              const c = ideas.filter((i) => i.stage === id).length;
              return (
                <div key={id} className="text-center">
                  <div className="text-[9px] uppercase tracking-[0.16em] truncate" style={{ color: INK_55 }}>{label}</div>
                  <div className="font-heading text-xl mt-1" style={{ fontVariationSettings: "'opsz' 48, 'wght' 450" }}>{c}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* AI Co-Pilot hero card */}
      <div
        className="card mt-4 flex flex-col md:flex-row md:items-center justify-between gap-4"
        style={{ background: "linear-gradient(135deg, rgba(216,168,87,0.06), transparent 60%)" }}
        data-testid="dashboard-ai-card"
      >
        <div className="flex items-start gap-3">
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(216,168,87,0.16)", border: "1px solid rgba(216,168,87,0.40)" }}
          >
            <Sparkles size={14} style={{ color: SEAL }} />
          </div>
          <div>
            <div className="eyebrow mb-1">v. AI Co-Pilot</div>
            <div className="font-heading text-xl" style={{ fontVariationSettings: "'opsz' 48, 'wght' 500" }}>
              Your AI <span className="font-display-italic">Co-Pilot</span>, ready to write.
            </div>
            <div className="text-xs mt-1" style={{ color: INK_55 }}>
              Hook, intro, body & CTA — tailored to your platform, tone, and audience. Powered by GPT-5.2.
            </div>
          </div>
        </div>
        <button onClick={() => setShowScript(true)} className="btn-primary flex-shrink-0" data-testid="dashboard-open-script" data-coach="ai-copilot">
          <Wand2 size={14} /> Open Co-Pilot
        </button>
      </div>

      {showNewIdea && (
        <IdeaModal onClose={() => setShowNewIdea(false)} onSaved={() => { setShowNewIdea(false); markCoachStep("create-idea"); load(); }} />
      )}
      {showOnboarding && (
        <OnboardingModal
          user={user}
          onClose={() => setOnboardingDismissed(true)}
          onComplete={() => { setOnboardingDismissed(true); refresh?.(); load(); }}
        />
      )}
      {showTour && (
        <ProductTour onClose={() => { setTourDismissed(true); refresh?.(); }} />
      )}
      {showScript && (
        <ScriptModal onClose={() => setShowScript(false)} onSaved={() => load()} />
      )}
    </div>
  );
}
